import type { TFunction } from 'i18next';

import type {
  IAggregatedStats,
  IConversionAttributionModelStats,
  IDailyStats,
} from '@feathr/blackbox';
import type { IDelta } from '@feathr/components';
import { moment, TimeFormat } from '@feathr/hooks';

import type {
  TAttributionModel,
  TDateRange,
  TTimeframePreset,
} from '../ReportsConversionsDashboardPage.utils';

export interface ITrendDataPoint {
  date: string;
  current: number;
  prior: number;
}

/**
 * Returns the immediately-preceding equal-length window before `range`.
 * For example, a 7-day range ending 2026-05-18 returns 2026-05-04 to 2026-05-10.
 */
export function calcPriorPeriod(range: TDateRange): TDateRange {
  const start = moment.utc(range[0]);
  const end = moment.utc(range[1]);
  // +1 because the span is inclusive of both endpoints
  const days = end.diff(start, 'days') + 1;
  const priorEnd = start.clone().subtract(1, 'day');
  const priorStart = priorEnd.clone().subtract(days - 1, 'days');
  return [priorStart.format(TimeFormat.isoDate), priorEnd.format(TimeFormat.isoDate)];
}

/**
 * Maps a delta direction to its display arrow.
 * Returns an empty string when there's no movement to display.
 */
export function getDeltaArrow(direction: IDelta['direction']): string {
  if (direction === 'increase') {
    return '▲';
  }
  if (direction === 'decrease') {
    return '▼';
  }
  return '';
}

export interface IDeltaStyleClasses {
  deltaIncrease: string;
  deltaDecrease: string;
  deltaNone: string;
}

/**
 * Returns the CSS module class name corresponding to a delta direction.
 */
export function getDeltaClassName(
  direction: IDelta['direction'],
  classes: IDeltaStyleClasses,
): string {
  if (direction === 'increase') {
    return classes.deltaIncrease;
  }
  if (direction === 'decrease') {
    return classes.deltaDecrease;
  }
  return classes.deltaNone;
}

/**
 * Formats a number as a currency string with no decimal places.
 * Defaults to en-US / USD. Intended for the Total Conversion Value tile.
 */
export function formatConversionValue(
  value: number,
  locale: string = 'en-US',
  currency: string = 'USD',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Reads the attribution model bucket from aggregated stats.
 * Returns `undefined` when stats or the requested model bucket are absent.
 */
export function getAttrStats(
  stats: IAggregatedStats | undefined,
  attr: TAttributionModel,
): IConversionAttributionModelStats | undefined {
  return stats?.conversions?.[attr];
}

/**
 * Returns the human-readable label for the current or prior date range
 * shown in the Conversions Trend chart legend.
 *
 * @example
 * formatRangeLabel('7d', range, false, t) // "Last 7 days"
 * formatRangeLabel('7d', range, true, t)  // "Previous 7 days"
 * formatRangeLabel('mtd', range, false, t) // "May 2026"
 */
export function formatRangeLabel(
  preset: TTimeframePreset,
  range: TDateRange,
  isPrior: boolean,
  t: TFunction,
): string {
  const targetRange = isPrior ? calcPriorPeriod(range) : range;
  const startMoment = moment.utc(targetRange[0]);
  const endMoment = moment.utc(targetRange[1]);

  switch (preset) {
    case '7d':
      return isPrior ? t('Previous 7 days') : t('Last 7 days');

    case '30d':
      return isPrior ? t('Previous 30 days') : t('Last 30 days');

    case '90d':
      return isPrior ? t('Previous 90 days') : t('Last 90 days');

    // mtd and last_month share the same "MMMM YYYY" label shape based on the
    // start of the target window. For mtd the window is the current month; for
    // last_month it's the previous full calendar month. Prior-period labels
    // correctly reflect the calendar month most of the prior window lands in.
    case 'mtd':

    case 'last_month':
      return startMoment.format('MMMM YYYY');

    case 'qtd': {
      const q = startMoment.quarter();
      const y = startMoment.year();
      return t('Q{{q}} {{y}}', { q, y });
    }

    case 'ytd':
      return t('{{year}} YTD', { year: startMoment.year() });

    case 'custom': {
      const s = startMoment.format('MMM D');
      const e = endMoment.format('MMM D');
      return `${s} – ${e}`;
    }
  }
}

/**
 * Maps an attribution model key to its display name for the "Attributed via ___" line.
 */
export function formatAttrLabel(attr: TAttributionModel, t: TFunction): string {
  switch (attr) {
    case 'full':
      return t('full attribution');

    case 'linear':
      return t('linear');

    case 'positional':
      return t('positional');

    case 'temporal':
      return t('temporal');

    case 'first_touch':
      return t('first click');

    case 'last_touch':
      return t('last click');
  }
}

/**
 * Merges current and prior daily stats by day-offset index into a unified
 * array suitable for a dual-series Recharts chart.
 */
export function mergeTrendData(
  currentData: IDailyStats[],
  priorData: IDailyStats[],
): ITrendDataPoint[] {
  return currentData.map((point, index) => ({
    date: point.metadata.date,
    current: point.num_conversions ?? 0,
    prior: priorData[index]?.num_conversions ?? 0,
  }));
}
